<template>
    <transition name="modal">
        <div class="benefits-modal--backdrop" v-if="showModal" @click="closeOnBackdrop">
            <div class="benefits-modal--container" @click.stop>
                <div class="benefits-modal--cross" @click="close"></div>

                <div class="benefits-modal-header--container">
                    <h1 class="benefits-modal-header">{{ headerTitle }}</h1>
                </div>

                <div class="benefits-modal-body">
                    <!-- Loading State -->
                    <div v-if="isLoading" class="benefits-loading">
                        <div class="preloader"></div>
                        <p>Loading your offers...</p>
                    </div>
                    
                    <!-- Error State -->
                    <div v-else-if="error" class="benefits-error">
                        <p>{{ error }}</p>
                    </div>
                    
                    <!-- Empty State -->
                    <div v-else-if="!hasOffers" class="benefits-empty">
                        <p>No offers available at this time.</p>
                        <p>{{ isPlusUser ? 'Check back later for exclusive FA+ benefits!' : 'Check back later for exclusive offers!' }}</p>
                    </div>
                    
                    <!-- Offers Display -->
                    <div v-else>
                        <!-- Featured Offers Section (if any) -->
                        <div v-if="featuredOffers.length" class="benefits-section">
                            <h3 class="benefits-section__title">Featured Offers</h3>
                            <div 
                                v-for="offer in featuredOffers" 
                                :key="offer.id"
                                class="benefits-card benefits-card--featured"
                                :class="{ 'revealed': revealedCode === offer.id }"
                                @click="revealCode(offer.id)"
                            >
                                <div class="benefits-card__icon">
                                    <img 
                                        v-if="offer.icon && offer.icon.type === 'image'" 
                                        :src="offer.icon.url" 
                                        :alt="offer.icon.alt"
                                        class="benefits-card__icon-img"
                                    />
                                    <span v-else class="benefits-card__icon-emoji">
                                        {{ (offer.icon && offer.icon.value) || '🎁' }}
                                    </span>
                                </div>
                                <div class="benefits-card__content">
                                    <h4 class="benefits-card__title">{{ offer.title }}</h4>
                                    <p class="benefits-card__description">{{ offer.description }}</p>
                                    
                                    <p 
                                        v-if="shouldShowDaysRemaining(offer.daysUntilExpiry)" 
                                        class="benefits-card__expiry"
                                    >
                                        ⏰ {{ offer.daysUntilExpiry }} days remaining
                                    </p>
                                    
                                    <!-- Offers WITH code: show code reveal/copy UI -->
                                    <div v-if="offer.code">
                                        <div v-if="revealedCode === offer.id" class="benefits-card__code-container">
                                            <button
                                                class="benefits-card__code"
                                                @click.stop="copyCode(offer.code, offer.id)"
                                                :class="{ 'copied': copiedCode === offer.id }"
                                            >
                                                <span class="benefits-card__code-text">{{ offer.code }}</span>
                                                <span class="benefits-card__copy-icon">
                                                    <span v-if="copiedCode === offer.id">✓</span>
                                                    <svg v-else width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                        <g transform="matrix(1 0 0 1 12 12)">
                                                            <path fill="currentColor" transform="translate(-12, -12)" d="M 4 2 C 2.895 2 2 2.895 2 4 L 2 18 L 4 18 L 4 4 L 18 4 L 18 2 L 4 2 z M 8 6 C 6.895 6 6 6.895 6 8 L 6 20 C 6 21.105 6.895 22 8 22 L 20 22 C 21.105 22 22 21.105 22 20 L 22 8 C 22 6.895 21.105 6 20 6 L 8 6 z M 8 8 L 20 8 L 20 20 L 8 20 L 8 8 z"/>
                                                        </g>
                                                    </svg>
                                                </span>
                                            </button>
                                            
                                            <button 
                                                v-if="offer.redemptionUrl" 
                                                class="benefits-card__redeem-btn"
                                                @click.stop="handleRedeemClick(offer)"
                                            >
                                                Redeem Now →
                                            </button>
                                        </div>
                                        <div v-else class="benefits-card__tap-prompt">
                                            Tap for code
                                        </div>
                                    </div>
                                    
                                    <!-- Offers WITHOUT code but WITH redemption URL: show tap to redeem -->
                                    <div v-else-if="!offer.code && offer.redemptionUrl" class="benefits-card__tap-prompt" @click.stop="handleRedeemClick(offer)">
                                        Tap to redeem
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Regular Offers Section -->
                        <div v-if="regularOffers.length" class="benefits-section">
                            <h3 class="benefits-section__title">All Offers</h3>
                            <div 
                                v-for="offer in regularOffers" 
                                :key="offer.id"
                                class="benefits-card"
                                :class="{ 'revealed': revealedCode === offer.id }"
                                @click="revealCode(offer.id)"
                            >
                                <div class="benefits-card__icon">
                                    <img 
                                        v-if="offer.icon && offer.icon.type === 'image'" 
                                        :src="offer.icon.url" 
                                        :alt="offer.icon.alt"
                                        class="benefits-card__icon-img"
                                    />
                                    <span v-else class="benefits-card__icon-emoji">
                                        {{ (offer.icon && offer.icon.value) || '🎁' }}
                                    </span>
                                </div>
                                <div class="benefits-card__content">
                                    <h4 class="benefits-card__title">{{ offer.title }}</h4>
                                    <p class="benefits-card__description">{{ offer.description }}</p>
                                    
                                    <p 
                                        v-if="shouldShowDaysRemaining(offer.daysUntilExpiry)" 
                                        class="benefits-card__expiry"
                                    >
                                        {{ offer.daysUntilExpiry }} days remaining
                                    </p>
                                    
                                    <!-- Only show code section if offer has a code -->
                                    <div v-if="offer.code">
                                        <div v-if="revealedCode === offer.id" class="benefits-card__code-container">
                                            <button
                                                class="benefits-card__code"
                                                @click.stop="copyCode(offer.code, offer.id)"
                                                :class="{ 'copied': copiedCode === offer.id }"
                                            >
                                                <span class="benefits-card__code-text">{{ offer.code }}</span>
                                                <span class="benefits-card__copy-icon">
                                                    <span v-if="copiedCode === offer.id">✓</span>
                                                    <svg v-else width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                        <g transform="matrix(1 0 0 1 12 12)">
                                                            <path fill="currentColor" transform="translate(-12, -12)" d="M 4 2 C 2.895 2 2 2.895 2 4 L 2 18 L 4 18 L 4 4 L 18 4 L 18 2 L 4 2 z M 8 6 C 6.895 6 6 6.895 6 8 L 6 20 C 6 21.105 6.895 22 8 22 L 20 22 C 21.105 22 22 21.105 22 20 L 22 8 C 22 6.895 21.105 6 20 6 L 8 6 z M 8 8 L 20 8 L 20 20 L 8 20 L 8 8 z"/>
                                                        </g>
                                                    </svg>
                                                </span>
                                            </button>
                                            
                                            <button 
                                                v-if="offer.redemptionUrl" 
                                                class="benefits-card__redeem-btn"
                                                @click.stop="handleRedeemClick(offer)"
                                            >
                                                Redeem Now →
                                            </button>
                                        </div>
                                        <div v-else class="benefits-card__tap-prompt">
                                            Tap for code
                                        </div>
                                    </div>
                                    
                                    <!-- Show redeem button even without code if redemption URL exists -->
                                    <button 
                                        v-if="!offer.code && offer.redemptionUrl" 
                                        class="benefits-card__redeem-btn benefits-card__redeem-btn--standalone"
                                        @click.stop="handleRedeemClick(offer)"
                                    >
                                        Redeem Now →
                                    </button>
                                    
                                    <a 
                                        v-if="offer.partner && offer.partner.icon && offer.partner.icon.type === 'image'" 
                                        :href="offer.partner.url || '#'" 
                                        :target="offer.partner.url ? '_blank' : '_self'"
                                        rel="noopener noreferrer"
                                        class="benefits-card__partner"
                                        @click.stop="offer.partner.url ? null : $event.preventDefault()"
                                    >
                                        <img 
                                            :src="offer.partner.icon.url" 
                                            :alt="offer.partner.icon.alt"
                                            class="benefits-card__partner-logo"
                                        />
                                    </a>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Instructions Section -->
                        <div class="benefits-section">
                            <h3 class="benefits-section__title">How to Redeem</h3>
                            <p class="benefits-instructions">
                                Tap on any offer to reveal your unique code. Click the code to copy it to your clipboard.
                                Use "Redeem Now" buttons to go directly to the partner's website.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import api from '../../api';

export default {
    name: 'BenefitsModal',
    props: {
        title: {
            type: String,
            default: 'FA+ Benefits & Offers'
        },
        isPlusUser: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            showModal: true,
            copiedCode: null,
            revealedCode: null,
            offers: [],
            isLoading: true,
            error: null,
        };
    },
    computed: {
        headerTitle() {
            return this.title;
        },
        hasOffers() {
            return this.offers.length > 0;
        },
        featuredOffers() {
            return this.offers.filter(offer => offer.isFeatured);
        },
        regularOffers() {
            return this.offers.filter(offer => !offer.isFeatured);
        }
    },
    methods: {
        toggleModal() {
            this.showModal = !this.showModal;
        },
        close() {
            this.showModal = false;
            this.$emit('close');
        },
        closeOnBackdrop(event) {
            if (event.target.classList.contains('benefits-modal--backdrop')) {
                this.close();
            }
        },
        revealCode(offerId) {
            this.revealedCode = offerId;
        },
        async copyCode(code, offerId) {
            try {
                await navigator.clipboard.writeText(code);
                this.copiedCode = offerId;
                setTimeout(() => {
                    this.copiedCode = null;
                }, 2000);
            } catch (err) {
                console.error('Failed to copy code:', err);
            }
        },
        async fetchOffers() {
            const CACHE_KEY = this.isPlusUser ? 'fa_plus_offers' : 'fa_free_offers';
            const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
            const cached = sessionStorage.getItem(CACHE_KEY);

            if (cached) {
                try {
                    const { data, timestamp } = JSON.parse(cached);
                    if (Date.now() - timestamp < CACHE_TTL) {
                        this.offers = data;
                        this.isLoading = false;
                        return;
                    }
                    sessionStorage.removeItem(CACHE_KEY);
                } catch (e) {
                    sessionStorage.removeItem(CACHE_KEY);
                }
            }

            try {
                const response = await api.crm.getOffers();

                if (response.status === 'success') {
                    this.offers = response.data || [];
                } else if (response.status === 'NoOffers') {
                    this.offers = [];
                } else {
                    this.offers = [];
                }

                sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data: this.offers, timestamp: Date.now() }));
                this.isLoading = false;
            } catch (err) {
                this.error = 'Unable to load offers. Please contact support.';
                this.isLoading = false;
                console.error('Failed to fetch offers:', err);
            }
        },
        shouldShowDaysRemaining(daysUntilExpiry) {
            return daysUntilExpiry !== null && daysUntilExpiry > 0 && daysUntilExpiry < 7;
        },
        handleRedeemClick(offer) {
            if (offer.redemptionUrl) {
                api.crm.trackOfferClick(offer.id);
                window.open(offer.redemptionUrl, '_blank', 'noopener,noreferrer');
            }
        },
        handleEscape(event) {
            if (event.key === 'Escape') {
                this.close();
            }
        }
    },
    mounted() {
        document.addEventListener('keydown', this.handleEscape);
        this.fetchOffers();
    },
    beforeDestroy() {
        document.removeEventListener('keydown', this.handleEscape);
    }
}
</script>

<style lang="scss" scoped>
    @import "~sass/_config.scss";
    @import "~sass/mixins/_media-query.scss";
    @import "~sass/_variables.scss";

    // Modal backdrop
    .benefits-modal--backdrop {
        z-index: 1000;
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;

        @include maxq(tablet) {
            align-items: flex-end;
            background: rgba(0, 0, 0, 0.6);
        }
    }

    // Modal container - Desktop: centered, Mobile: bottom sheet
    .benefits-modal--container {
        z-index: 1010;
        width: 700px;
        max-width: 90%;
        max-height: 85vh;
        position: relative;
        box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.16);
        border: solid 1px $col-neutral--5;
        background-color: $white;
        overflow-y: auto;
        border-radius: 8px;

        @include maxq(tablet) {
            // Bottom sheet on mobile - full screen
            width: 100%;
            max-width: 100%;
            max-height: 100vh;
            height: 100vh;
            border-radius: 20px 20px 0 0;
            border-bottom: none;
            animation: slideUp 0.3s ease-out;
        }
    }

    // Slide up animation for mobile
    @keyframes slideUp {
        from {
            transform: translateY(100%);
        }
        to {
            transform: translateY(0);
        }
    }

    // Modal transitions
    .modal-enter-active {
        transition: opacity 0.3s ease;

        .benefits-modal--container {
            transition: transform 0.3s ease;
        }
    }

    .modal-leave-active {
        transition: opacity 0.3s ease;

        .benefits-modal--container {
            transition: transform 0.3s ease;
        }
    }

    .modal-enter, .modal-leave-to {
        opacity: 0;

        .benefits-modal--container {
            @include maxq(tablet) {
                transform: translateY(100%);
            }
        }
    }

    // Modal header
    .benefits-modal-header--container {
        display: flex;
        flex: 1;
        padding: 40px 30px 20px 30px;
        justify-content: center;
        text-align: center;
        border-bottom: 1px solid #e9ecef;

        @include maxq(tablet) {
            padding: 30px 20px 20px 20px;
        }
    }


    .benefits-modal-header {
        font-size: 28px;
        font-weight: 600;
        color: $col-neutral--8;
        margin: 0;

        @include maxq(tablet) {
            font-size: 22px;
        }
    }

    // Close button
    .benefits-modal--cross {
        position: absolute;
        width: 26px;
        height: 26px;
        padding: 10px;
        top: 15px;
        right: 15px;
        cursor: pointer;
        z-index: 1020;

        &::before,
        &::after {
            content: "";
            display: flex;
            width: 20px;
            height: 2px;
            position: absolute;
            left: 3px;
            top: 12px;
            background: #75746b;
        }

        &::before {
            transform: rotate(45deg);
        }

        &::after {
            transform: rotate(-45deg);
        }

        &:hover {
            &::before,
            &::after {
                background: #000000;
            }
        }
    }

    // Modal body
    .benefits-modal-body {
        padding: 30px;

        @include maxq(tablet) {
            padding: 20px;
        }
    }

    // Benefits sections
    .benefits-section {
        margin-bottom: 30px;

        &:last-child {
            margin-bottom: 0;
        }

        &__title {
            font-size: 18px;
            font-weight: 600;
            color: $col-neutral--8;
            margin: 0 0 16px 0;
            padding-bottom: 8px;
            border-bottom: 2px solid #28a745;

            @include maxq(tablet) {
                font-size: 16px;
            }
        }
    }

    // Benefits card
    .benefits-card {
        display: flex;
        gap: 16px;
        padding: 20px;
        background: #f8f9fa;
        border: 1px solid #e9ecef;
        border-radius: 8px;
        margin-bottom: 12px;
        transition: all 0.2s ease;
        cursor: pointer;

        &:last-child {
            margin-bottom: 0;
        }

        &:hover:not(.revealed) {
            background: #e9ecef;
            transform: translateY(-1px);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        &.revealed {
            background: #fff;
            border-color: #28a745;
        }

        &__icon {
            font-size: 32px;
            flex-shrink: 0;

            @include maxq(tablet) {
                font-size: 28px;
            }
        }

        &__content {
            flex: 1;
        }

        &__title {
            font-size: 16px;
            font-weight: 600;
            color: $col-neutral--8;
            margin: 0 0 6px 0;

            @include maxq(tablet) {
                font-size: 15px;
            }
        }

        &__description {
            font-size: 14px;
            color: $col-neutral--6;
            margin: 0 0 10px 0;
            line-height: 1.4;

            @include maxq(tablet) {
                font-size: 13px;
            }
        }

        &__code {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            font-size: 13px;
            font-weight: 600;
            color: #28a745;
            background: white;
            padding: 6px 12px;
            border-radius: 4px;
            border: 1px solid #28a745;
            cursor: pointer;
            transition: all 0.2s ease;

            &:hover {
                background: #28a745;
                color: white;
                transform: scale(1.02);
                box-shadow: 0 2px 8px rgba(40, 167, 69, 0.3);
            }

            &.copied {
                background: #28a745;
                color: white;
            }

            @include maxq(tablet) {
                font-size: 12px;
                padding: 5px 10px;
                gap: 6px;
            }
        }

        &__code-text {
            font-family: 'Courier New', monospace;
            letter-spacing: 0.5px;
        }

        &__copy-icon {
            display: flex;
            align-items: center;
            flex-shrink: 0;
        }

        &__tap-prompt {
            margin-top: 8px;
            font-size: 12px;
            font-weight: 600;
            color: #28a745;
            text-transform: uppercase;
            letter-spacing: 0.5px;

            @include maxq(tablet) {
                font-size: 11px;
            }
        }

        &__code-container {
            margin-top: 10px;
            animation: slideDown 0.2s ease-out;
        }
    }

    @keyframes slideDown {
        from {
            opacity: 0;
            transform: translateY(-10px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    // Benefits list
    .benefits-list {
        &__item {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            padding: 12px 0;
            border-bottom: 1px solid #e9ecef;

            &:last-child {
                border-bottom: none;
            }
        }

        &__icon {
            color: #28a745;
            font-weight: bold;
            font-size: 16px;
            flex-shrink: 0;
        }

        &__text {
            font-size: 14px;
            color: $col-neutral--7;
            line-height: 1.5;

            @include maxq(tablet) {
                font-size: 13px;
            }
        }
    }

    // Instructions
    .benefits-instructions {
        font-size: 14px;
        line-height: 1.6;
        color: $col-neutral--6;
        margin: 0;
        padding: 16px;
        background: #f8f9fa;
        border-left: 3px solid #007bff;
        border-radius: 4px;

        @include maxq(tablet) {
            font-size: 13px;
            padding: 12px;
        }
    }

    // Loading state
    .benefits-loading {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 60px 20px;
        text-align: center;
        
        .preloader {
            margin-bottom: 16px;
        }
        
        p {
            font-size: 14px;
            color: $col-neutral--6;
            margin: 0;
        }
    }

    // Error state
    .benefits-error {
        padding: 40px 20px;
        text-align: center;
        background: #fff3cd;
        border: 1px solid #ffc107;
        border-radius: 8px;
        margin: 20px;
        
        p {
            font-size: 14px;
            color: #856404;
            margin: 0;
            line-height: 1.5;
        }
    }

    // Empty state
    .benefits-empty {
        padding: 60px 20px;
        text-align: center;
        
        p {
            font-size: 14px;
            color: $col-neutral--6;
            margin: 0 0 8px 0;
            line-height: 1.6;
            
            &:last-child {
                margin-bottom: 0;
            }
        }
    }

    // Featured card styling
    .benefits-card--featured {
        border: 2px solid #ffc107;
        background: linear-gradient(135deg, #fff9e6 0%, #ffffff 100%);
    }

    // Section badge for featured
    .benefits-section__badge {
        margin-right: 6px;
        font-size: 16px;
    }

    // Icon image styling
    .benefits-card__icon-img {
        width: 32px;
        height: 32px;
        object-fit: contain;
        font-size: 10px;

        @include maxq(tablet) {
            width: 28px;
            height: 28px;
        }
    }

    .benefits-card__icon-emoji {
        font-size: 32px;
        
        @include maxq(tablet) {
            font-size: 28px;
        }
    }

    // Expiry warning
    .benefits-card__expiry {
        font-size: 12px;
        font-weight: 600;
        color: #dc3545;
        margin: 6px 0;
        
        @include maxq(tablet) {
            font-size: 11px;
        }
    }

    // Redeem button
    .benefits-card__redeem-btn {
        display: inline-block;
        margin-left: 8px;
        padding: 6px 12px;
        background: #007bff;
        color: white;
        border: none;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        
        &:hover {
            background: #0056b3;
            transform: translateY(-1px);
            box-shadow: 0 2px 6px rgba(0, 123, 255, 0.3);
        }
        
        &--standalone {
            display: block;
            margin-left: 0;
            margin-top: 12px;
            text-align: center;
        }
        
        @include maxq(tablet) {
            display: block;
            margin-left: 0;
            margin-top: 8px;
            text-align: center;
        }
    }

    // Partner logo
    .benefits-card__partner {
        display: block;
        margin-top: 12px;
        padding-top: 12px;
        border-top: 1px solid #e9ecef;
        
        &-logo {
            max-width: 80px;
            max-height: 30px;
            object-fit: contain;
            opacity: 0.8;
            transition: opacity 0.2s ease;
        }
        
        &:hover &-logo {
            opacity: 1;
        }
    }
</style>
